import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";
// GAVION: B2-I2 release the dialer lease on sign-out (spec 04 §2.4)
import {
  cachedSalesId,
  releaseDialerOnSignOut,
} from "@/components/gavion/dialer/releaseOnSignOut";

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const sale = await getSale();

      if (sale == null) {
        throw new Error();
      }

      return {
        id: sale.id,
        fullName: `${sale.first_name} ${sale.last_name}`,
        avatar: sale.avatar?.src,
      };
    },
  });

// To speed up checks, we cache the initialization state
// and the current sale in the local storage. They are cleared on logout.
const IS_INITIALIZED_CACHE_KEY = "RaStore.auth.is_initialized";
const CURRENT_SALE_CACHE_KEY = "RaStore.auth.current_sale";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

export async function getIsInitialized() {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(IS_INITIALIZED_CACHE_KEY);
  if (cachedValue != null) {
    return cachedValue === "true";
  }

  const { data, error } = await getSupabaseClient()
    .from("init_state")
    .select("is_initialized");
  // GAVION: a failed lookup is not "not initialized" (that path signs the user
  // out and opens the sign-up page). Sign-up stays invite-only server-side.
  if (error) return true;
  const isInitialized = data?.at(0)?.is_initialized > 0;

  if (isInitialized) {
    storage?.setItem(IS_INITIALIZED_CACHE_KEY, "true");
  }

  return isInitialized;
}

// GAVION: fresh (uncached) check that the signed-in account is not disabled.
// Fails closed: any lookup error or missing row signs the user out.
const ACTIVE_CHECK_TTL_MS = 60_000;
let lastActiveCheck: { userId: string; at: number } | null = null;

const isNetworkError = (e: unknown) => {
  const err = (e ?? {}) as { name?: string; status?: number };
  return (
    e instanceof TypeError ||
    err.status === 0 ||
    err.name === "AuthRetryableFetchError"
  );
};

// A transient failure (network, Auth/REST 5xx or timeout) must never sign the
// user out: during a Supabase incident that logged every session out, on every
// device. Data stays protected server-side (RLS excludes disabled accounts).
const isTransient = (e: unknown) => {
  const status = (e as { status?: number } | null)?.status;
  return isNetworkError(e) || (typeof status === "number" && status >= 500);
};

export async function assertSaleActive({ force = false } = {}) {
  const client = getSupabaseClient();
  const fail = async (message: string) => {
    lastActiveCheck = null;
    // local scope: only this browser; a global sign-out would also kill the
    // user's other tabs and devices (including a session they just opened).
    await client.auth.signOut({ scope: "local" });
    clearCache();
    throw { redirectTo: "/login", message };
  };
  let sale: { disabled?: boolean } | null = null;
  let userId: string;
  try {
    const { data: userData, error: userError } = await client.auth.getUser();
    if (userError && isTransient(userError)) return;
    if (!userData?.user) return fail("gavion.login.errors.unknown");
    userId = userData.user.id;
    if (
      !force &&
      lastActiveCheck?.userId === userId &&
      Date.now() - lastActiveCheck.at < ACTIVE_CHECK_TTL_MS
    ) {
      return;
    }
    const res = await client
      .from("sales")
      .select("disabled")
      .match({ user_id: userId })
      .maybeSingle();
    if (res.error) {
      if (isTransient(res.error)) return;
      return fail("gavion.login.errors.unknown");
    }
    sale = res.data;
  } catch (e) {
    if (isTransient(e)) return;
    return fail("gavion.login.errors.unknown");
  }
  if (!sale) return fail("gavion.login.errors.unknown");
  if (sale.disabled) return fail("gavion.login.errors.disabled");
  lastActiveCheck = { userId, at: Date.now() };
}

const getSale = async () => {
  const storage = getLocalStorage();
  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  // Shouldn't happen after login but just in case
  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }
  const userId = dataSession.session.user.id;

  // GAVION: the cache belongs to one auth user. A sign-in that skips logout
  // (invite / recovery activation in a browser used by someone else) must not
  // reuse the previous user's sale: the app would act as the wrong person.
  const cachedValue = storage?.getItem(CURRENT_SALE_CACHE_KEY);
  if (cachedValue != null) {
    try {
      const cached = JSON.parse(cachedValue);
      if (cached?.user_id === userId) return cached;
    } catch {
      // corrupted cache: refetch below
    }
    storage?.removeItem(CURRENT_SALE_CACHE_KEY);
  }

  const { data: dataSale, error: errorSale } = await getSupabaseClient()
    .from("sales")
    .select(
      "id, user_id, first_name, last_name, avatar, administrator, disabled",
    )
    .match({ user_id: userId })
    .single();

  // Shouldn't happen either as all users are sales but just in case
  if (dataSale == null || errorSale) {
    return undefined;
  }

  storage?.setItem(CURRENT_SALE_CACHE_KEY, JSON.stringify(dataSale));
  return dataSale;
};

function clearCache() {
  lastActiveCheck = null;
  const storage = getLocalStorage();
  storage?.removeItem(IS_INITIALIZED_CACHE_KEY);
  storage?.removeItem(CURRENT_SALE_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      const result = await baseAuthProvider.login(params);
      // GAVION: a disabled account is signed out right after the token is issued
      await assertSaleActive({ force: true });
      return result;
    },
    logout: async () => {
      // GAVION: B2-I2 best effort, bounded; the cached sale is read before clearCache
      await releaseDialerOnSignOut(
        getSupabaseClient(),
        cachedSalesId(getLocalStorage()?.getItem(CURRENT_SALE_CACHE_KEY)),
      );
      clearCache();
      // GAVION: this browser only. ra-supabase signs out globally, which also
      // ended the user's other tabs and devices (and any session just opened).
      await getSupabaseClient().auth.signOut({ scope: "local" });
    },
    // GAVION: only a dead session signs the user out. ra-supabase also logged
    // out on 403, but a 403 is "this role may not read that column/table" (RLS,
    // column grants): one restricted query on a page (e.g. Intégrations) ended
    // the session, and the post-login redirect back to that page looped.
    checkError: async (error) => {
      const e = (error ?? {}) as { status?: number; name?: string };
      if (
        e.status === 401 ||
        (e.status === 400 && e.name === "AuthSessionMissingError")
      ) {
        throw error;
      }
    },
    checkAuth: async (params) => {
      // Users are on the set-password page, nothing to do
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password")
      ) {
        return;
      }
      // GAVION: invite / recovery activation page (public, verifies on click)
      if (
        window.location.pathname === "/activer" ||
        window.location.hash.startsWith("#/activer")
      ) {
        return;
      }
      // Users are on the forgot-password page, nothing to do
      if (
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      // Users are on the sign-up page, nothing to do
      if (
        window.location.pathname === "/sign-up" ||
        window.location.hash.includes("#/sign-up")
      ) {
        return;
      }

      const isInitialized = await getIsInitialized();

      if (!isInitialized) {
        await getSupabaseClient().auth.signOut({ scope: "local" });
        throw {
          redirectTo: "/sign-up",
          message: false,
        };
      }

      await baseAuthProvider.checkAuth(params);
      // GAVION: covers OAuth sessions and sessions that outlive a disable
      await assertSaleActive();
    },
    // GAVION: role used by RequireAdmin (a disabled account has no role)
    getPermissions: async () => {
      const sale = await getSale();
      if (sale == null || sale.disabled) return "disabled";
      return sale.administrator ? "admin" : "rep";
    },
    canAccess: async (params) => {
      const isInitialized = await getIsInitialized();
      if (!isInitialized) return false;

      // Get the current user
      const sale = await getSale();
      if (sale == null) return false;

      // Compute access rights from the sale role
      const role = sale.administrator ? "admin" : "user";
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
